import { useCustomPageStore } from "@/store/customPage";
import { useUserStore } from "@/store/user";
import { useFeatureStore } from "@/store/feature";
import { getCookie, setCookie } from "@/utils/cookies";
import { defineAsyncComponent, markRaw, type Component } from "vue";
import PageLoader from "@/components/v2/page-loader/index.vue";
import { LAYOUT_MAIN, LAYOUT_V2 } from "@/utils/variables";
import { getDocumentUrl } from "@/utils/mediaHelper";
import i18n from "@/i18n";
import PagesService from "@/api/services/pagesService";
import { Landing } from "@/interfaces/Landing.ts";
import { CustomPage } from "@/interfaces/Pages/CustomPage.ts";
import { MiddlewareInterface } from "@/interfaces/Middleware.ts";
import { useLandingStore } from "@/store/landing.ts";

const checkCustomRouteAccess = async (route: string, page: CustomPage) => {
  if (!page.has_password) {
    if (page.password) {
      setCookie(route, page.password);
    }

    return page;
  }

  const password = prompt("Please Enter Password", "");
  page = await PagesService.get(route, password);

  if (page.has_password) {
    alert("Invalid password!");
    page = await checkCustomRouteAccess(route, page);
  }

  setCookie(route, page.password as string);

  return page;
};

const asyncCustomPage = (loader: () => Promise<Component>) =>
  markRaw(
    defineAsyncComponent({
      loader,
      loadingComponent: PageLoader,
      delay: 0,
    }),
  );

export default async ({ to, redirect, pinia }: MiddlewareInterface) => {
  if (to.name !== "CustomPage") {
    return;
  }

  let component;
  let attrs = {};

  const isLoggedIn = useUserStore(pinia).isLoggedIn;
  const customPageStore = useCustomPageStore(pinia);

  try {
    const password = getCookie(to.path);
    const lang = i18n.global.locale as string;
    let page = customPageStore.getCustomPage(to.path, lang);

    if (!page) {
      page = await customPageStore.fetchCustomPage(to.path, password || null, lang);
    }

    if (page.type === "landing") {
      const landing = page.landing as Landing;
      const landingStore = useLandingStore(pinia);

      landingStore.setLandingPage(landing);

      component = asyncCustomPage(() => import("@/pages/landing/index.vue"));
      attrs = { id: landing.id };

      //todo: temp solution, remove once v2 layout is adapted to logged in users
      if (isLoggedIn) {
        return redirect(useFeatureStore(pinia).getLandingUrl);
      }

      to.meta.landing_id = landing.id;
      to.meta.layout = LAYOUT_V2;
    } else {
      page = await checkCustomRouteAccess(to.path, page);

      if (page.type === "transforming_links") {
        window.location.href = page.source;
        return;
      }

      to.meta.layout = isLoggedIn ? LAYOUT_MAIN : LAYOUT_V2;
      component = asyncCustomPage(() => import("@/pages/embedded-document/index.vue"));
      attrs = { embedded: getDocumentUrl(page.source) };
    }
  } catch {
    component = asyncCustomPage(() => import("@/pages/page404/index.vue"));
    to.meta.layout = isLoggedIn ? LAYOUT_MAIN : LAYOUT_V2;
  }

  const props = { component, attrs };
  customPageStore.addProps(to.path, props);
};
